/// <reference types="vite/client" />
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { Session, User } from '@supabase/supabase-js';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  dbUser: {
    plan: string;
    subscription_status: string;
    credits: number;
  } | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshDbUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [dbUser, setDbUser] = useState<AuthContextType['dbUser']>(null);
  const [loading, setLoading] = useState(true);

  // Fetch dbUser details from public.users
  const fetchDbUser = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('plan, subscription_status, credits')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching dbUser:', error.message);
      } else if (data) {
        setDbUser(data);
      }
    } catch (err) {
      console.error('Failed to fetch dbUser:', err);
    }
  };

  // Sync user metadata to public.users table (Frontend Fallback)
  const syncUserToDatabase = async (currentUser: User) => {
    try {
      const { error } = await supabase.from('users').upsert(
        {
          id: currentUser.id,
          email: currentUser.email || null,
        },
        { onConflict: 'id' }
      );
      if (error) {
        console.error('Error syncing user to DB:', error.message);
      } else {
        await fetchDbUser(currentUser.id);
      }
    } catch (err) {
      console.error('Failed to sync user to database:', err);
    }
  };

  const refreshDbUser = async () => {
    if (user) {
      await fetchDbUser(user.id);
    }
  };

  useEffect(() => {
    // Check active sessions
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        syncUserToDatabase(session.user);
      }
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await syncUserToDatabase(session.user);
      } else {
        setDbUser(null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setDbUser(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ session, user, dbUser, loading, signOut, refreshDbUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
